import {
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: 'input[percentInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PercentInputDirective),
      multi: true,
    },
  ],
})
export class PercentInputDirective implements ControlValueAccessor {
  private onChange: (val: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private isDisabled = false;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {}

  // ---- ControlValueAccessor ----
  writeValue(modelValue: number | string | null | undefined): void {
    // Convert model (e.g., 0.1) -> view (e.g., "10")
    const numeric = this.parseToNumber(modelValue);
    if (numeric === null) {
      this.setViewValue('');
      return;
    }
    this.setViewValue(this.normalizeNumber(numeric * 100));
  }

  registerOnChange(fn: (val: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  // ---- DOM <-> model sync ----
  @HostListener('input', ['$event.target.value'])
  handleInput(viewValue: string) {
    // Convert view (e.g., "10") -> model (e.g., 0.1)
    const parsed = this.parseToNumber(viewValue);
    const modelVal = parsed === null ? null : parsed / 100;
    this.onChange(modelVal);
  }

  @HostListener('blur')
  handleBlur() {
    this.onTouched();
    // Optional: reformat on blur to trimmed/normalized number
    const current = this.el.nativeElement.value;
    const parsed = this.parseToNumber(current);
    this.setViewValue(parsed === null ? '' : this.normalizeNumber(parsed));
  }

  // ---- helpers ----
  private setViewValue(v: string | number) {
    this.renderer.setProperty(this.el.nativeElement, 'value', String(v));
  }

  private normalizeNumber(n: number): string {
    // Avoid trailing zeros like "10.0000"
    const asNum = Number(String(n));
    if (!Number.isFinite(asNum)) {
      return '';
    }

    if (Number.isInteger(asNum)) {
      return String(asNum);
    }

    const fixed = +asNum.toFixed(6);
    return String(fixed).replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.$/, '');
  }

  private parseToNumber(
    value: number | string | null | undefined
  ): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const cleaned = value.replace(/[%\s]/g, '').replace(',', '.');
    const parsed = Number.parseFloat(cleaned);

    return Number.isFinite(parsed) ? parsed : null;
  }
}
